import { Component, Input, OnChanges } from '@angular/core';
import {
  AgentKey,
  ExperimentPage,
  ExperimentProduct,
  PageVisibilityCounts,
} from '../agentic-ecommerce.types';
import {
  getPageVisibilityCounts,
  getProducts,
  isPdpPageType,
  isProductMissingForPage,
  isProductVisibleForPage,
} from '../agentic-ecommerce.utils';

@Component({
  selector: 'app-agentic-page-group',
  templateUrl: './page-group.component.html',
  styleUrls: ['./page-group.component.css'],
})
export class PageGroupComponent implements OnChanges {
  @Input() page: ExperimentPage | null = null;
  @Input() selectedAgentKey: AgentKey = '';
  @Input() selectedAgentLabel: string = '';
  @Input() showMissingOnly: boolean = false;

  private readonly productPageSize: number = 24;
  public displayedProductLimit: number = this.productPageSize;

  ngOnChanges(): void {
    this.displayedProductLimit = this.productPageSize;
  }

  public get products(): ExperimentProduct[] {
    const products = getProducts(this.page);

    if (!this.showMissingOnly) {
      return products;
    }

    return products.filter(
      (product) =>
        isProductMissingForPage(
          product,
          this.selectedAgentKey,
          this.rawPageType,
        ),
    );
  }

  public get displayedProducts(): ExperimentProduct[] {
    return this.products.slice(0, this.displayedProductLimit);
  }

  public get displayedProductCount(): number {
    return this.displayedProducts.length;
  }

  public get hasMoreProducts(): boolean {
    return this.displayedProductCount < this.products.length;
  }

  public get footnoteText(): string {
    const totalProducts = this.products.length;
    const shownProducts = this.displayedProductCount;

    if (totalProducts > this.productPageSize) {
      return `${shownProducts} of ${totalProducts} ${this.footnoteProductLabel}`;
    }

    if (this.showMissingOnly && this.isPdpPage) {
      return `${totalProducts} ${this.productWord(totalProducts)} with gaps`;
    }

    if (this.showMissingOnly) {
      return `${totalProducts} missing ${this.productWord(totalProducts)}`;
    }

    return `${totalProducts} ${this.productWord(totalProducts)}`;
  }

  private get footnoteProductLabel(): string {
    if (this.showMissingOnly && this.isPdpPage) {
      return 'products with gaps';
    }

    if (this.showMissingOnly) {
      return 'missing products';
    }

    return 'products';
  }

  public get counts(): PageVisibilityCounts {
    return getPageVisibilityCounts(this.page, this.selectedAgentKey);
  }

  public get progressWidth(): number {
    if (!this.counts.totalCount) {
      return 0;
    }

    return Math.round(
      (this.counts.visibleCount / this.counts.totalCount) * 100,
    );
  }

  public get pageType(): string {
    return String(this.rawPageType || 'Page').toUpperCase();
  }

  public get rawPageType(): string {
    return String(this.page?.pageType || '');
  }

  public get isPdpPage(): boolean {
    return isPdpPageType(this.rawPageType);
  }

  public get visibleSummaryLabel(): string {
    return this.isPdpPage ? 'matched' : 'visible';
  }

  public get missingBadgeLabel(): string {
    return this.isPdpPage ? 'with gaps' : 'missing';
  }

  public get pageTitle(): string {
    return this.page?.pageTitle || 'Untitled page';
  }

  public get pagePath(): string {
    return this.page?.pagePath || this.page?.pageUrl || 'Unknown path';
  }

  public get pageUrl(): string {
    return this.page?.pageUrl || '';
  }

  public isVisible(product: ExperimentProduct): boolean {
    return isProductVisibleForPage(
      product,
      this.selectedAgentKey,
      this.rawPageType,
    );
  }

  public trackByProduct(index: number, product: ExperimentProduct): string {
    return product.url || product.name || String(index);
  }

  public showMoreProducts(): void {
    this.displayedProductLimit += this.productPageSize;
  }

  private productWord(count: number): string {
    return count === 1 ? 'product' : 'products';
  }
}
